import { GiroType } from "@/types";

/**
 * Why a giro number was rejected. The digit count and the check digit get
 * separate messages, because "7-8 siffror" is actively misleading when the
 * customer typed eight digits and only the check digit was wrong.
 */
export type GiroValidity = "valid" | "format" | "checkDigit";

/** Removes spaces and hyphens used as visual separators in giro/OCR numbers. */
const stripSeparators = (value: string) => value.replace(/[\s-]/g, "");

/** Mod-10 (Luhn) check used by Swedish bankgiro and plusgiro numbers. */
const isLuhnValid = (digits: string) => {
	let sum = 0;
	let double = false;
	for (let i = digits.length - 1; i >= 0; i--) {
		let n = Number(digits[i]);
		if (double) {
			n *= 2;
			if (n > 9) n -= 9;
		}
		sum += n;
		double = !double;
	}
	return sum % 10 === 0;
};

export const validateBankgiro = (value: string): GiroValidity => {
	const digits = stripSeparators(value);
	if (!/^\d{7,8}$/.test(digits)) return "format";
	return isLuhnValid(digits) ? "valid" : "checkDigit";
};

export const validatePlusgiro = (value: string): GiroValidity => {
	const digits = stripSeparators(value);
	if (!/^\d{2,8}$/.test(digits)) return "format";
	return isLuhnValid(digits) ? "valid" : "checkDigit";
};

/**
 * The printed form a giro number is submitted in, whatever separators the
 * customer typed: bankgiro `5050-1055`, plusgiro `902003-3`.
 */
export const formatGiro = (value: string, giroType: GiroType): string => {
	const digits = stripSeparators(value);
	const suffixLength = giroType === "plusgiro" ? 1 : 4;
	if (digits.length <= suffixLength) return digits;
	return `${digits.slice(0, -suffixLength)}-${digits.slice(-suffixLength)}`;
};

/**
 * Reads the giro type back from a submitted number: `formatGiro` prints a
 * plusgiro with a one-digit suffix and a bankgiro with four.
 */
export const inferGiroType = (value: string): GiroType => {
	const suffix = value.trim().match(/-(\d+)$/)?.[1];
	if (suffix) return suffix.length === 1 ? "plusgiro" : "bankgiro";

	return validateBankgiro(value) !== "valid" &&
		validatePlusgiro(value) === "valid"
		? "plusgiro"
		: "bankgiro";
};

/** OCR/loan numbers carry no check digit, so digits alone are enough. */
export const isValidOcr = (value?: string): boolean => {
	if (!value) return true;
	if (!/^\d+$/.test(value)) return false;
	return mod10(value);
};

const mod10 = (value: string): boolean => {
	const sum = value
		.split("")
		.reverse()
		.reduce((acc, digit, i) => {
			const product = i % 2 === 0 ? parseInt(digit) : 2 * parseInt(digit);
			return acc + (product > 9 ? product - 9 : product);
		}, 0);

	return sum > 0 && sum % 10 === 0;
};
